<template>
    <div class="resource-management">
        <div class="resource-management__header">
            <div class="resource-management__header-title-wrapper">
            </div>
            <div class="resource-management__header-actions">
                <resource-search-with-filter
                    :value="searchParam"
                    :search-help-popover-text="searchHelpPopoverText"
                    :has-search-feature="hasSearchFeature"
                    :is-searching="isSearching"
                    :is-collection-loading="isCollectionLoading"
                    @run:search="searchForTheFirstTime"
                >
                    <template #filters>
                        <slot name="filters" v-bind="{ applyFilters }"></slot>
                    </template>
                </resource-search-with-filter>

                <slot name="actions" v-bind="{ isCollectionLoading }"></slot>
            </div>
        </div>

        <resource-table
            :tableColumns="tableColumns"
            :selectAllRowsFeature="selectAllRowsFeature"
            :is-sort-enable="isSortEnable"
            :is-collection-loading="isCollectionLoading"
            :has-loading-error="hasLoadingError"
            :collection="collection"
            @change:sort="sortChanged"
            @change:select-all="selectAllChange"
        >
            <template #table-row="{ entity }">
                <slot name="resource-table-row" v-bind="{ entity }"></slot>
            </template>
        </resource-table>

        <cursor-pagination-v2
            v-if="!isCollectionLoading"
            v-model:cursor="cursor"
            :loading="loadingPagination"
            :firstPage="firstPageUrl"
            :nextPage="nextPageUrl"
            :prevPage="prevPageUrl"
            :lastPage="lastPageUrl"
            @change="changeCursor"
        ></cursor-pagination-v2>
    </div>
</template>

<script>
import ResourceSearchWithFilter from './ResourceSearchWithFilter.vue';
import ResourceTable from './ResourceTable.vue';
import CursorPaginationV2 from '~/components/general/CursorPaginationV2.vue';
import { camelCase } from '~/utils/string';
import clientRoutes from './../../mixins/client-routes';
import scrollTo from './../../mixins/scroll-to';

export default {
    mixins: [clientRoutes, scrollTo],
    components: {
        ResourceSearchWithFilter,
        ResourceTable,
        CursorPaginationV2
    },
    props: {
        selectAllRowsFeature: Boolean,
        hasSearchFeature: Boolean,
        searchIncludeParams: Array,

        pageUrl: {
            type: String,
        },
        searchUrl: String,

        simpleSearch: Boolean, // AKA Legacy ?
        searchHelpPopoverText: { type: String, default: '' },
        isSortEnable: {
            type: Boolean,
            default: false,
        },

        tableTitle: { type: String, required: true },
        entityName: { type: String, required: true },
        tableColumns: { type: Array, required: true },

        entityFetchClient: { type: Function, required: true },
        entitySearchClient: { type: Function, required: false },
    },
    watch: {
        collection: {
            deep: true,
            handler(collection) {
                this.$emit('collection-changed', collection);
            },
        },
    },
    data() {
        return {
            teamId: Spark.state.currentTeam.id,

            isCollectionLoading: true,
            hasLoadingError: false,

            collection: [],

            cursor: null,
            firstPageUrl: null,
            prevPageUrl: null,
            nextPageUrl: null,
            lastPageUrl: null,
            loadingPagination: false,

            stash: null,

            showSearch: false,

            searchParam: '',
            filterParams: {},
            isSearchMode: false,
            firstTimeSearching: true,
            isSearching: false,
        };
    },
    mounted() {
        const route = this.currentClientRoute();

        if (this.simpleSearch && route && route.hash == 'search') {
            const decodedQuery = decodeURI(route.parameters[0]);

            this.searchParam = decodedQuery && decodedQuery !== 'null' ? decodedQuery : '';
            this.search(this.searchParam);
        }
    },
    created() {
        const eventEntity = camelCase(this.entityName);

        Bus.$on(`${eventEntity}Created`, (entity) => {
            this.collection.unshift(entity);
        });

        Bus.$on(`${eventEntity}Updated`, (entity) => {
            const index = this.collection.findIndex((row) => row.id === entity.id);
            this.collection[index] = entity;
        });

        Bus.$on(`${eventEntity}Deleted`, ({ id }) => {
            this.collection = this.collection.filter((row) => row.id !== id);
        });

        Bus.$on(`${eventEntity}UpdateResourceTable`, ({ cursor, searchParam, filters }) => {
            this.cursor = cursor;

            if (searchParam || filters) {
                if (filters) {
                    this.filterParams = filters;
                }
                this.search(searchParam);
            } else {
                this.loadRecords();
            }
        });
    },
    methods: {
        changeCursor() {
            if (this.isSearchMode) {
                this.search(this.searchParam);
            } else {
                this.loadRecords();
            }
        },
        loadRecords() {
            this.hasLoadingError = false;
            this.loadingPagination = true;
            this.isCollectionLoading = true;

            this.entityFetchClient(this.teamId, this.cursor, this.getSortDir())
                .then(({ data }) => {
                    if (data.data.length) {
                        this.collection = data.data;
                        this.firstPageUrl = data.first_page_url;
                        this.prevPageUrl = data.prev_page_url;
                        this.nextPageUrl = data.next_page_url;
                        this.lastPageUrl = data.last_page_url;

                        this.updateRouting();
                    }
                })
                .catch((error) => {
                    this.hasLoadingError = true;
                    console.log(error);
                })
                .finally(() => {
                    this.isCollectionLoading = false;
                    this.loadingPagination = false;
                    this.scrollToPageTop();
                });
        },
        searchForTheFirstTime(searchParam) {
            this.firstTimeSearching = true;
            this.search(searchParam);
        },
        search(searchParam) {
            this.searchParam = searchParam ? decodeURI(searchParam) : '';

            if (!this.searchParam && !Object.keys(this.filterParams).length) {
                this.unloadSearchData();

                return this.loadRecords();
            }

            this.isSearchMode = true;
            this.loadingPagination = true;
            this.isSearching = true;
            this.isCollectionLoading = true;

            const params = {
                cursor: this.firstTimeSearching ? null : this.cursor,
                include: this.searchIncludeParams,
                ...this.filterParams,
            };

            if (this.isSortEnable && this.getSortDir() === 'desc') {
                params.sort_dir = 'desc';
            }

            if (this.simpleSearch) {
                params['query'] = this.searchParam;
            } else if (this.entityName.toLowerCase() === 'contact' && [100404, 100818, 101824, 102472, 103150].includes(this.teamId)) {
                params['filter[phone]'] = this.searchParam;
            } else {
                params['filter[search]'] = this.searchParam;
            }

            this.entitySearchClient(this.teamId, params)
                .then(({ data }) => {
                    if (this.firstTimeSearching) {
                        this.stash = {};
                        this.stash.collection = this.collection;
                        this.stash.firstPageUrl = this.firstPageUrl;
                        this.stash.prevPageUrl = this.prevPageUrl;
                        this.stash.nextPageUrl = this.nextPageUrl;
                        this.stash.lastPageUrl = this.lastPageUrl;
                        this.stash.cursor = this.cursor;
                        this.cursor = null;
                    }
                    this.firstTimeSearching = false;

                    this.collection = data.data;
                    this.firstPageUrl = data.first_page_url;
                    this.prevPageUrl = data.prev_page_url;
                    this.nextPageUrl = data.next_page_url;
                    this.lastPageUrl = data.last_page_url;

                    this.updateRouting();
                })
                .catch((error) => {
                    this.$toasted.global.platform_error();
                    console.error(error);
                })
                .finally(() => {
                    this.isCollectionLoading = false;
                    this.loadingPagination = false;
                    this.isSearching = false;

                    this.scrollToPageTop();
                });
        },
        unloadSearchData() {
            this.isSearchMode = false;
            this.searchParam = '';
            this.filterParams = {};

            if (this.stash) {
                this.collection = this.stash.collection;
                this.firstPageUrl = this.stash.firstPageUrl;
                this.prevPageUrl = this.stash.prevPageUrl;
                this.nextPageUrl = this.stash.nextPageUrl;
                this.lastPageUrl = this.stash.lastPageUrl;
                this.cursor = this.stash.cursor;
                this.stash = null;
            }
        },
        updateRouting() {
            const cursor = this.cursor ? '/' + this.cursor : '';

            if (this.searchParam) {
                this.showClientRoute(`#/${this.searchUrl ? this.searchUrl : 'search'}/${this.searchParam}${cursor}`);
            } else {
                this.showClientRoute(`#/${this.pageUrl ? this.pageUrl : 'pages'}${cursor}`);
            }
        },
        selectAllChange(event) {
            this.collection = this.collection.map((entity) => ({
                ...entity,
                selected: event.target.checked,
            }));
        },
        applyFilters(filterParams) {
            this.filterParams = filterParams;
            this.search(this.searchParam);
        },
        sortChanged(tableColumn) {
            this.$emit('sortChanged', tableColumn);

            this.cursor = null;
            this.updateRouting();

            this.search(this.searchParam);
        },
        getSortDir() {
            return this.tableColumns.find((item) => item.sort)?.sort;
        },
    },
};
</script>

<style lang="scss">
.resource-management {
    position: relative;
    background-color: var(--color-white);
    padding: 24px;
}
.resource-management__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 36px;
}

.resource-management__header-title-wrapper {
    display: flex;
    align-items: center;
    gap: 24px;
}

.resource-management__header-title {
    font-family: var(--font-dm-sans);
    font-weight: var(--weight-semibold);
    font-size: 1.4rem;
    white-space: nowrap;
}
.resource-management__header-count {
    font-family: var(--font-sans);
    font-weight: var(--weight-semibold);
    color: var(--color-grey);
    font-size: 0.8125rem;
}

.resource-management__header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}
</style>
